/**
 * Tournament config.
 *
 * ONLY FIELDS THE BACKEND ACTUALLY HAS. Three things a settings screen might
 * be expected to carry are deliberately absent:
 *
 *   name, format  — no such columns. A tournament is identified by night
 *                   number, date and season, and V2 is always group into
 *                   knockout, so there is no format to choose.
 *   groups, qualifiers,
 *   wildcards     — settled at draw time, not here. All three depend on who
 *                   actually checks in, so they belong to the check-in modal
 *                   on the night. Sending them to this endpoint is a 400.
 *   auto knockout — retired. The bracket is always generated when generation
 *                   is called; the wildcard review it used to gate lives in
 *                   the Fixtures tab.
 *   placement pts — placement_points_config is keyed by VENUE, not tournament,
 *                   and has no CRUD endpoint. Editing it from one tournament's
 *                   settings would silently rescore every other tournament at
 *                   the venue, so it belongs on a venue screen.
 *
 * PUT refuses outside 'upcoming' and 'registration' (EDITABLE_STATUSES in the
 * endpoint), so once a draw exists this renders read-only rather than offering
 * a save that will 409.
 */

import { useState } from 'react';
import { apiPut } from '../../lib/api.js';
import LegCountFields, { KNOCKOUT_ROUNDS } from './LegCountFields.jsx';

/** Mirrors EDITABLE_STATUSES in tournaments/[tournamentId]/index.js. */
const EDITABLE_STATUSES = ['upcoming', 'registration'];

/** '' -> null, so a cleared field defers to the venue default, not to zero. */
const orNull = (value) => (value === '' || value === null ? null : value);

const formFrom = (t) => ({
  nightNumber:        t.night_number ?? '',
  date:               t.date ?? '',
  maxEntries:         t.max_entries,
  entryFee:           t.entry_fee,
  groupBestOfLegs:    t.group_best_of_legs,
  r32BestOfLegs:      t.r32_best_of_legs,
  r16BestOfLegs:      t.r16_best_of_legs,
  qfBestOfLegs:       t.qf_best_of_legs,
  sfBestOfLegs:       t.sf_best_of_legs,
  finalBestOfLegs:    t.final_best_of_legs,
  autoTournamentCompletion: t.auto_tournament_completion === 1,
  rulesText:          t.rules_text ?? '',
});

export default function SettingsTab({ venueId, tournamentId, tournament, onChanged }) {
  const [form, setForm]   = useState(() => formFrom(tournament));
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const locked = !EDITABLE_STATUSES.includes(tournament.status);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const setChecked = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.checked }));

  const onSubmit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);

    const { error: err } = await apiPut(
      `/api/venues/${venueId}/tournaments/${tournamentId}`,
      {
        nightNumber:        orNull(form.nightNumber),
        date:               orNull(form.date),
        maxEntries:         orNull(form.maxEntries),
        entryFee:           orNull(form.entryFee),
        groupBestOfLegs:    orNull(form.groupBestOfLegs),
        ...Object.fromEntries(
          KNOCKOUT_ROUNDS.map(({ field }) => [field, orNull(form[field])])
        ),
        autoTournamentCompletion: form.autoTournamentCompletion,
        rulesText:                form.rulesText.trim() || null,
      }
    );

    setBusy(false);
    if (err) { setError(err); return; }
    setNotice('Settings saved.');
    await onChanged();
  };

  return (
    <form className="hd-admin-form hd-stack" onSubmit={onSubmit}>
      {locked && (
        <p className="hd-hint">
          Config is locked once a draw exists — the matches already reference
          these numbers. Tournament status is '{tournament.status}'.
        </p>
      )}

      <div className="hd-fields">
        <div className="hd-field">
          <label className="hd-label" htmlFor="set-night">Night number</label>
          <input id="set-night" className="hd-input" type="number" min="1"
                 value={form.nightNumber} onChange={set('nightNumber')} disabled={locked} />
        </div>

        <div className="hd-field">
          <label className="hd-label" htmlFor="set-date">Date</label>
          <input id="set-date" className="hd-input" type="date"
                 value={form.date} onChange={set('date')} disabled={locked} />
        </div>

        <div className="hd-field">
          <label className="hd-label" htmlFor="set-max">Max entries</label>
          <input id="set-max" className="hd-input" type="number" min="1"
                 value={form.maxEntries} onChange={set('maxEntries')} disabled={locked} />
        </div>

        <div className="hd-field">
          <label className="hd-label" htmlFor="set-fee">Entry fee</label>
          <input id="set-fee" className="hd-input" type="number" min="0" step="0.01"
                 value={form.entryFee} onChange={set('entryFee')} disabled={locked} />
        </div>
      </div>

      <h3 className="hd-section-title">Format</h3>

      <p className="hd-hint">
        Groups and knockout size are chosen on the night, when the tournament
        is started.
      </p>

      <LegCountFields form={form} set={set} idPrefix="set-legs" disabled={locked} />

      <h3 className="hd-section-title">Automation</h3>

      <label className="hd-row" style={{ gap: 8, fontSize: 13 }}>
        <input type="checkbox" checked={form.autoTournamentCompletion}
               onChange={setChecked('autoTournamentCompletion')} disabled={locked} />
        Complete the tournament automatically when the final is played
      </label>

      <div className="hd-field">
        <label className="hd-label" htmlFor="set-rules">Rules text</label>
        <textarea id="set-rules" className="hd-textarea" value={form.rulesText}
                  onChange={set('rulesText')} disabled={locked} />
        <span className="hd-hint">Optional — falls back to the venue's rules when empty.</span>
      </div>

      {error  && <div className="hd-badge hd-badge-danger"  role="alert">{error}</div>}
      {notice && <div className="hd-badge hd-badge-success" role="status">{notice}</div>}

      {!locked && (
        <div className="hd-row">
          <button className="hd-btn hd-btn-primary" type="submit" disabled={busy}>
            {busy ? 'Saving…' : 'Save settings'}
          </button>
        </div>
      )}
    </form>
  );
}
